import React, { useState, useEffect } from 'react';
import { Box, Typography, Drawer, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Menu } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import OrgSelector from './OrgSelector';

const SIDEBAR_WIDTH = 230;

const AUTH_NAV = [
  { label: 'Overview', path: '/overview' },
  { label: 'Claims', path: '/claims' },
  { label: 'Patients', path: '/patients' },
  { label: 'Providers', path: '/providers' },
  { label: 'Appeals', path: '/appeals' },
  { label: 'Approvals', path: '/approvals' },
  { label: 'Denial Analysis', path: '/denial-analysis' },
  { label: 'PT Notes', path: '/notes' },
  { label: 'Upload Batches', path: '/upload-batches', adminOnly: true },
];

const BOTTOM_NAV = [
  { label: 'Team', path: '/team' },
  { label: 'Profile', path: '/profile' },
  { label: 'Billing', path: '/billing' },
];

const UNAUTH_NAV = [
  { label: 'Login', path: '/login' },
  { label: 'Sign Up', path: '/signup' },
];

const NavLink = ({ label, path, active, onClick }) => (
  <Typography
    component={Link}
    to={path}
    onClick={onClick}
    sx={{
      fontFamily: 'var(--ab-font-sans)',
      fontSize: 20,
      color: 'var(--ab-fg)',
      textDecoration: 'none',
      fontWeight: active ? 700 : 400,
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      paddingLeft: active ? '10px' : 0,
      transition: 'padding-left 0.15s, font-weight 0.15s',
      '&:hover': { fontWeight: 600 },
      '&::before': active
        ? {
            content: '""',
            position: 'absolute',
            left: 0,
            width: '3px',
            height: '14px',
            background: 'var(--ab-fg)',
          }
        : undefined,
    }}
  >
    {label}
  </Typography>
);

const SidebarContent = ({ isAuthenticated, onLogout, username, isAdmin, activeOrgId, isActive, onLinkClick }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      height: '100%',
      px: 'var(--ab-space-3)',
      py: 'var(--ab-space-8)',
      boxSizing: 'border-box',
    }}
  >
    {/* Logo + Nav */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '65px', px: 'var(--ab-space-3)' }}>
      {/* Logo */}
      <Box
        component={Link}
        to={isAuthenticated ? '/overview' : '/'}
        onClick={onLinkClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--ab-space-2)',
          textDecoration: 'none',
        }}
      >
        <Box
          sx={{
            bgcolor: 'var(--ab-fg)',
            borderRadius: 'var(--ab-radius-md)',
            width: 40,
            height: 40,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              color: 'var(--ab-fg-inverse)',
              fontFamily: 'var(--ab-font-sans)',
              fontWeight: 900,
              fontSize: 18,
              letterSpacing: '0.02em',
            }}
          >
            M
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: 'var(--ab-font-sans)',
            fontWeight: 700,
            fontSize: 20,
            letterSpacing: '-0.01em',
            color: 'var(--ab-fg)',
          }}
        >
          Meduri
        </Typography>
      </Box>

      {/* Primary nav */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--ab-space-3)' }}>
        {(isAuthenticated ? AUTH_NAV.filter((item) => !item.adminOnly || isAdmin) : UNAUTH_NAV).map((item) => (
          <NavLink
            key={item.path}
            label={item.label}
            path={item.path}
            active={isActive(item.path)}
            onClick={onLinkClick}
          />
        ))}
      </Box>
    </Box>

    {/* Bottom: profile + billing + logout + user chip */}
    {isAuthenticated && (
      <Box>
        <Box sx={{ borderTop: '1px solid var(--ab-border-hair)', mb: 'var(--ab-space-3)' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--ab-space-2)', px: 'var(--ab-space-3)', mb: 'var(--ab-space-3)' }}>
          {BOTTOM_NAV.map((item) => (
            <NavLink
              key={item.path}
              label={item.label}
              path={item.path}
              active={isActive(item.path)}
              onClick={onLinkClick}
            />
          ))}
          <Typography
            onClick={onLogout}
            sx={{
              fontFamily: 'var(--ab-font-sans)',
              fontSize: 20,
              color: 'var(--ab-fg)',
              cursor: 'pointer',
              fontWeight: 400,
              '&:hover': { fontWeight: 600 },
            }}
          >
            Logout
          </Typography>
        </Box>
        <Box sx={{ borderTop: '1px solid var(--ab-border-hair)', mb: 'var(--ab-space-3)' }} />
        <Box sx={{ px: 'var(--ab-space-3)', mb: 'var(--ab-space-3)' }}>
          <OrgSelector activeOrgId={activeOrgId} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--ab-space-2)', px: 'var(--ab-space-3)' }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: 'var(--ab-bg-disabled)',
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: 'var(--ab-font-sans)',
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--ab-fg)',
                lineHeight: 1.2,
              }}
            >
              {username || 'Account'}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'var(--ab-font-sans)',
                fontSize: 11,
                color: 'var(--ab-fg-subtle)',
              }}
            >
              Provider
            </Typography>
          </Box>
        </Box>
      </Box>
    )}
  </Box>
);

const SidebarLayout = ({ isAuthenticated, onLogout, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeOrgId, setActiveOrgId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      api
        .get('/accounts/profile/')
        .then((res) => {
          setUsername(res.data?.username || res.data?.email || '');
          setIsAdmin(res.data?.is_staff === true);
          setActiveOrgId(res.data?.active_organization || null);
        })
        .catch(() => { setUsername(''); setIsAdmin(false); setActiveOrgId(null); });
    }
  }, [isAuthenticated]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
      return;
    }
    localStorage.removeItem('token');
    navigate('/login');
  };

  const closeDrawer = () => setDrawerOpen(false);

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <IconButton
          aria-label="open navigation"
          onClick={() => setDrawerOpen(true)}
          sx={{ position: 'fixed', top: 12, left: 12, zIndex: 1300, color: 'var(--ab-fg)' }}
        >
          <Menu size={20} />
        </IconButton>
        <Drawer open={drawerOpen} onClose={closeDrawer} PaperProps={{ sx: { width: SIDEBAR_WIDTH } }}>
          <SidebarContent
            isAuthenticated={isAuthenticated}
            onLogout={handleLogout}
            username={username}
            isAdmin={isAdmin}
            activeOrgId={activeOrgId}
            isActive={isActive}
            onLinkClick={closeDrawer}
          />
        </Drawer>
        <Box component="main" sx={{ flexGrow: 1, p: 'var(--ab-space-6)' }}>
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        component="aside"
        sx={{
          width: SIDEBAR_WIDTH,
          minWidth: SIDEBAR_WIDTH,
          borderRight: 'var(--ab-hairline)',
          boxSizing: 'border-box',
          height: '100vh',
          position: 'sticky',
          top: 0,
          background: 'var(--ab-bg)',
        }}
      >
        <SidebarContent
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          username={username}
          isAdmin={isAdmin}
          activeOrgId={activeOrgId}
          isActive={isActive}
        />
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 'var(--ab-space-6)' }}>
        {children}
      </Box>
    </Box>
  );
};

export default SidebarLayout;
